'use client';

import { useState, useMemo } from 'react';
import states from '@/data/ifta-states.json';

const JURISDICTIONS = Object.values(states)
  .map((s) => ({ abbr: s.abbr, name: s.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const blankRow = (id) => ({ id, jurisdiction: '', miles: '', gallons: '', rate: '' });

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  (n < 0 ? '-' : '') + '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fixed = (n, d = 2) => n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 15,
  border: '1px solid var(--border-primary)',
  borderRadius: 8,
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
};
const thStyle = { textAlign: 'left', fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', padding: '0 8px 8px' };
const tdStyle = { padding: '4px 8px', verticalAlign: 'middle' };

export default function IftaCalculator() {
  const [rows, setRows] = useState([blankRow(1), blankRow(2), blankRow(3)]);
  const [nextId, setNextId] = useState(4);

  const update = (id, field, value) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const addRow = () => {
    setRows((rs) => [...rs, blankRow(nextId)]);
    setNextId((n) => n + 1);
  };
  const removeRow = (id) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));
  const reset = () => {
    setRows([blankRow(nextId), blankRow(nextId + 1), blankRow(nextId + 2)]);
    setNextId((n) => n + 3);
  };

  const result = useMemo(() => {
    const totalMiles = rows.reduce((s, r) => s + num(r.miles), 0);
    const totalGallons = rows.reduce((s, r) => s + num(r.gallons), 0);
    const mpg = totalGallons > 0 ? totalMiles / totalGallons : 0;
    const lines = rows.map((r) => {
      const miles = num(r.miles);
      const gallons = num(r.gallons);
      const taxableGallons = mpg > 0 ? miles / mpg : 0;
      const netGallons = taxableGallons - gallons;
      return { id: r.id, taxableGallons, netGallons, tax: netGallons * num(r.rate) };
    });
    const net = lines.reduce((s, l) => s + l.tax, 0);
    return { totalMiles, totalGallons, mpg, lines, net };
  }, [rows]);

  const ready = result.totalMiles > 0 && result.totalGallons > 0;

  return (
    <div
      style={{
        margin: '8px 0 40px',
        padding: 24,
        border: '1px solid var(--border-primary)',
        borderRadius: 16,
        background: 'var(--bg-secondary, var(--bg-primary))',
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead>
            <tr>
              <th style={thStyle}>State / Province</th>
              <th style={thStyle}>Miles driven</th>
              <th style={thStyle}>Gallons purchased</th>
              <th style={thStyle}>Tax rate ($/gal)</th>
              <th style={thStyle}>Taxable gal.</th>
              <th style={thStyle}>Tax due / credit</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const l = result.lines[i];
              return (
                <tr key={r.id}>
                  <td style={tdStyle}>
                    <input
                      list="ifta-jurisdictions"
                      aria-label="State or province"
                      style={inputStyle}
                      value={r.jurisdiction}
                      placeholder="e.g. TX"
                      onChange={(e) => update(r.id, 'jurisdiction', e.target.value)}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      min="0"
                      inputMode="decimal"
                      aria-label="Miles driven"
                      style={inputStyle}
                      value={r.miles}
                      onChange={(e) => update(r.id, 'miles', e.target.value)}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      min="0"
                      inputMode="decimal"
                      aria-label="Gallons purchased"
                      style={inputStyle}
                      value={r.gallons}
                      onChange={(e) => update(r.id, 'gallons', e.target.value)}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      inputMode="decimal"
                      aria-label="Tax rate per gallon"
                      style={inputStyle}
                      value={r.rate}
                      placeholder="0.0000"
                      onChange={(e) => update(r.id, 'rate', e.target.value)}
                    />
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 15 }}>
                    {ready ? fixed(l.taxableGallons) : '–'}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: 700,
                      fontSize: 15,
                      color: ready && l.tax < 0 ? '#15803d' : 'var(--text-primary)',
                    }}
                  >
                    {ready && num(r.rate) > 0 ? money(l.tax) : '–'}
                  </td>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => removeRow(r.id)}
                      aria-label="Remove row"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <datalist id="ifta-jurisdictions">
        {JURISDICTIONS.map((j) => (
          <option key={j.abbr} value={j.abbr}>
            {j.name}
          </option>
        ))}
      </datalist>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0 24px' }}>
        <button type="button" onClick={addRow} className="btn-secondary" style={{ cursor: 'pointer' }}>
          + Add jurisdiction
        </button>
        <button type="button" onClick={reset} className="btn-secondary" style={{ cursor: 'pointer' }}>
          Clear
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
        aria-live="polite"
      >
        {[
          ['Total miles', fixed(result.totalMiles, 0)],
          ['Total gallons', fixed(result.totalGallons)],
          ['Fleet MPG', ready ? fixed(result.mpg) : '–'],
          [result.net < 0 ? 'Net credit' : 'Net tax due', ready ? money(result.net) : '–'],
        ].map(([label, value]) => (
          <div
            key={label}
            style={{ padding: 16, borderRadius: 12, background: 'var(--bg-primary)', border: '1px solid var(--border-primary)' }}
          >
            <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '16px 0 0', lineHeight: 1.6 }}>
        Estimate only. Enter the current quarter's rate for each jurisdiction from the official IFTA rate table.
        Surcharges and rounding rules can change the filed amount. Nothing you type is stored or sent anywhere.
      </p>
    </div>
  );
}
